/**
 * WASM module loader for DECKSTR
 * 
 * This module loads and initializes the WASM binary and provides
 * type-safe wrappers around the WASM functions.
 */

import type {
  DeckAnalysisRequest,
  CompleteAnalysis,
  FormatTranslation,
  AnchorSuggestion,
  HeatmapValidation,
} from './wasm-types';

interface DeckstrWasm {
  analyze_deck_wasm(request_json: string): string;
  translate_format(
    from_format: number,
    to_format: number,
    from_lands: number,
    from_nonlands: number
  ): string;
  suggest_anchor(lands: number, nonlands: number, deck_size: number): string;
  validate_heatmap(
    heatmap_csv: string,
    lands: number,
    nonlands: number,
    actual_weight: number
  ): string;
}

let wasmModule: DeckstrWasm | null = null;
let wasmLoadPromise: Promise<DeckstrWasm> | null = null;

/**
 * Load the WASM module
 */
export async function loadWasm(): Promise<DeckstrWasm> {
  if (wasmModule) {
    return wasmModule;
  }

  if (wasmLoadPromise) {
    return wasmLoadPromise;
  }

  wasmLoadPromise = (async () => {
    try {
      // Load WASM module from ui/pkg directory (one level up from ui/lib/)
      const init = await import('../pkg/deckstrgg_core.js');
      await init.default();

      wasmModule = {
        analyze_deck_wasm: init.analyze_deck_wasm,
        translate_format: init.translate_format,
        suggest_anchor: init.suggest_anchor,
        validate_heatmap: init.validate_heatmap,
      };

      console.log('✓ DECKSTR WASM loaded successfully');
      return wasmModule!;
    } catch (error) {
      console.error('Failed to load WASM module:', error);
      throw new Error('WASM module could not be loaded. Make sure to build the WASM first with: make wasm');
    }
  })();

  return wasmLoadPromise;
}

/**
 * Analyze a deck using the WASM backend
 */
export async function analyzeDeck(
  request: DeckAnalysisRequest
): Promise<CompleteAnalysis> {
  const wasm = await loadWasm();

  try {
    const requestJson = JSON.stringify(request);
    const resultJson = wasm.analyze_deck_wasm(requestJson);
    const result = JSON.parse(resultJson);
    return result;
  } catch (error) {
    console.error('WASM analyze_deck error:', error);
    throw new Error(`Analysis failed: ${error}`);
  }
}

/**
 * Translate deck format (e.g., Limited → Standard)
 */
export async function translateFormat(
  fromFormat: number,
  toFormat: number,
  fromLands: number,
  fromNonlands: number
): Promise<FormatTranslation> {
  const wasm = await loadWasm();

  try {
    const resultJson = wasm.translate_format(
      fromFormat,
      toFormat,
      fromLands,
      fromNonlands
    );
    const result = JSON.parse(resultJson);
    return result;
  } catch (error) {
    console.error('WASM translate_format error:', error);
    throw new Error(`Format translation failed: ${error}`);
  }
}

/**
 * Suggest nearest anchor for deck configuration
 */
export async function suggestAnchor(
  lands: number,
  nonlands: number,
  deckSize: number
): Promise<AnchorSuggestion> {
  const wasm = await loadWasm();

  try {
    const resultJson = wasm.suggest_anchor(lands, nonlands, deckSize);
    const result = JSON.parse(resultJson);
    return result;
  } catch (error) {
    console.error('WASM suggest_anchor error:', error);
    throw new Error(`Anchor suggestion failed: ${error}`);
  }
}

/**
 * Validate deck against heatmap
 */
export async function validateHeatmap(
  heatmapCsv: string,
  lands: number,
  nonlands: number,
  actualWeight: number
): Promise<HeatmapValidation> {
  const wasm = await loadWasm();

  try {
    const resultJson = wasm.validate_heatmap(
      heatmapCsv,
      lands,
      nonlands,
      actualWeight
    );
    const result = JSON.parse(resultJson);
    return result;
  } catch (error) {
    console.error('WASM validate_heatmap error:', error);
    throw new Error(`Heatmap validation failed: ${error}`);
  }
}

/**
 * Check if WASM is loaded
 */
export function isWasmLoaded(): boolean {
  return wasmModule !== null;
}

/**
 * Preload WASM module (call this on app initialization)
 */
export async function preloadWasm(): Promise<void> {
  try {
    await loadWasm();
  } catch (error) {
    console.warn('WASM preload failed, will retry on first use:', error);
  }
}

